import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';

import { useControlledDropdown } from '@/lomavisSharedUi/hooks/useControlledDropdown';
import { OverlayTemplateFolder } from '@/lomavisSharedUi/types/overlayTemplates';

export interface LomavisTemplateFolderSidebarLabels {
  navigation: string;
  title: string;
  allTemplates: string;
  newFolder: string;
  folderActions: string;
  renameFolder: string;
  deleteFolder: string;
}

export interface LomavisTemplateFolderSidebarProps {
  folders: OverlayTemplateFolder[];
  selectedFolderId: string | null;
  labels: LomavisTemplateFolderSidebarLabels;
  canManageFolders?: boolean;
  isLoading?: boolean;
  onFolderSelect: (folderId: string | null) => void;
  onCreateFolder?: () => void;
  onRenameFolder?: (folder: OverlayTemplateFolder) => void;
  onDeleteFolder?: (folder: OverlayTemplateFolder) => void;
}

const FolderActionsDropdown: React.FC<{
  folder: OverlayTemplateFolder;
  isSelected: boolean;
  labels: LomavisTemplateFolderSidebarLabels;
  onRenameFolder?: (folder: OverlayTemplateFolder) => void;
  onDeleteFolder?: (folder: OverlayTemplateFolder) => void;
}> = ({ folder, isSelected, labels, onRenameFolder, onDeleteFolder }) => {
  const { close, containerRef, show, toggle } = useControlledDropdown();

  return (
    <div ref={containerRef} className="flex-shrink-0 me-2">
      <Dropdown align="end" show={show} onToggle={() => {}}>
        <Dropdown.Toggle
          type="button"
          size="sm"
          bsPrefix="btn"
          variant="link"
          className={isSelected ? 'text-primary' : 'text-muted'}
          aria-label={`${labels.folderActions}: ${folder.name}`}
          onClick={toggle}
        >
          <i className="fa-regular fa-ellipsis-vertical" aria-hidden="true" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {onRenameFolder && (
            <Dropdown.Item
              as="button"
              aria-label={`${labels.renameFolder}: ${folder.name}`}
              onClick={() => {
                close();
                onRenameFolder(folder);
              }}
            >
              {labels.renameFolder}
            </Dropdown.Item>
          )}
          {onDeleteFolder && (
            <Dropdown.Item
              as="button"
              className="text-danger"
              aria-label={`${labels.deleteFolder}: ${folder.name}`}
              onClick={() => {
                close();
                onDeleteFolder(folder);
              }}
            >
              {labels.deleteFolder}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

/**
 * Organism: one-level folder navigation for an overlay-template library.
 */
export const LomavisTemplateFolderSidebar: React.FC<
  LomavisTemplateFolderSidebarProps
> = ({
  folders,
  selectedFolderId,
  labels,
  canManageFolders = false,
  isLoading = false,
  onFolderSelect,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder
}) => {
  const selectedClasses = 'bg-light-primary text-primary fw-semibold';
  const defaultClasses = 'text-dark';

  return (
    <aside
      className="h-100 bg-light overflow-auto"
      aria-label={labels.navigation}
    >
      <div className="d-flex flex-column gap-3 p-4 pb-3">
        <h2 className="text-uppercase text-muted fw-bold fs-8 mb-0">
          {labels.title}
        </h2>
        {canManageFolders && onCreateFolder && (
          <Button
            type="button"
            size="sm"
            variant="outline-secondary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={onCreateFolder}
          >
            <i className="fa-regular fa-plus" aria-hidden="true" />
            {labels.newFolder}
          </Button>
        )}
      </div>

      <nav
        className="d-flex flex-column gap-1 px-3 pb-4"
        aria-label={labels.navigation}
      >
        <button
          type="button"
          className={`btn btn-sm text-start py-2 px-3 ${
            selectedFolderId === null ? selectedClasses : defaultClasses
          }`}
          aria-current={selectedFolderId === null ? 'page' : undefined}
          onClick={() => onFolderSelect(null)}
        >
          <i className="fa-regular fa-grid-2 me-2" aria-hidden="true" />
          {labels.allTemplates}
        </button>

        {isLoading && (
          <div className="placeholder-wave p-3" aria-label={labels.title}>
            <span className="placeholder col-10 mb-3 rounded bg-secondary bg-opacity-25" />
            <span className="placeholder col-8 mb-3 rounded bg-secondary bg-opacity-25" />
            <span className="placeholder col-9 rounded bg-secondary bg-opacity-25" />
          </div>
        )}

        {!isLoading &&
          folders.map((folder) => {
            const isSelected = selectedFolderId === folder.id;

            return (
              <div
                key={folder.id}
                className={`d-flex align-items-center rounded ${
                  isSelected ? selectedClasses : defaultClasses
                }`}
              >
                <button
                  type="button"
                  className={`btn btn-sm border-0 d-flex align-items-center text-start flex-grow-1 overflow-hidden py-2 ps-3 pe-2 ${
                    isSelected ? 'text-primary fw-semibold' : 'text-dark'
                  }`}
                  aria-current={isSelected ? 'page' : undefined}
                  title={folder.name}
                  onClick={() => onFolderSelect(folder.id)}
                >
                  <i
                    className="fa-regular fa-folder flex-shrink-0 me-2"
                    aria-hidden="true"
                  />
                  <span className="d-block text-truncate">{folder.name}</span>
                </button>

                {canManageFolders && (onRenameFolder || onDeleteFolder) && (
                  <FolderActionsDropdown
                    folder={folder}
                    isSelected={isSelected}
                    labels={labels}
                    onRenameFolder={onRenameFolder}
                    onDeleteFolder={onDeleteFolder}
                  />
                )}
              </div>
            );
          })}
      </nav>
    </aside>
  );
};
